import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { mapTypeCodeToCategorie } from '@/lib/constants/operation-types';

// Sprint 6.9.4 — Imports CSV Caisse Trip Sun (26 cols) + Caisse B2C (20 cols).
// CRITIQUE : tous les inserts via le client supabase initialisé (pas de fetch raw),
// sinon erreur "No API key found in request" sur PostgREST.

// =====================================================================
// Helpers parsing (réutilisent les conventions Sprint 6.9.1.2)
// =====================================================================

export function parseNumberDZD(raw: string | number | null | undefined): number {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number') return raw;
  const cleaned = raw.toString().replace(/[^\d-]/g, '');
  if (!cleaned || cleaned === '-') return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export function parseDateFr(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.toString().trim();
  if (!s) return null;
  // 2024-02-01
  const m1 = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m1) return `${m1[1]}-${m1[2]}-${m1[3]}`;
  // 01/02/2024 ou 1-2-2024
  const m2 = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})/.exec(s);
  if (m2) {
    const [, d, mo, yRaw] = m2;
    const y = (yRaw ?? '').length === 2 ? `20${yRaw}` : (yRaw ?? '');
    return `${y}-${(mo ?? '').padStart(2, '0')}-${(d ?? '').padStart(2, '0')}`;
  }
  const dt = new Date(s);
  return isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10);
}

export function parseTimeHHMM(raw: string | null | undefined): string {
  if (!raw) return '12:00:00';
  const s = raw.toString().trim();
  const m = /^(\d{1,2})[h:]?(\d{2})?/.exec(s);
  if (!m) return '12:00:00';
  const hh = (m[1] ?? '12').padStart(2, '0');
  const mm = (m[2] ?? '00').padStart(2, '0');
  return `${hh}:${mm}:00`;
}

function buildTimestamp(
  dateRaw: string | null | undefined,
  timeRaw: string | null | undefined,
): string {
  const date = parseDateFr(dateRaw) ?? new Date().toISOString().slice(0, 10);
  return `${date}T${parseTimeHHMM(timeRaw)}`;
}

// =====================================================================
// Mappings Trip Sun (immigration)
// =====================================================================

const TRIP_SUN_DEPENSE_MAP: { regex: RegExp; type: string }[] = [
  { regex: /prime/i, type: 'prime_teleoperatrice' },
  { regex: /boost/i, type: 'boost_facebook_immigration' },
  { regex: /cnas/i, type: 'cnas' },
  { regex: /notaire/i, type: 'notaire' },
  { regex: /location|loyer/i, type: 'location_siege' },
  { regex: /pdg|directeur|direction/i, type: 'pdg' },
  { regex: /salaire/i, type: 'salaire_fixe' },
  { regex: /achat|fourniture/i, type: 'achat_fournitures' },
  { regex: /carurant|carburant|essence/i, type: 'carburant' },
  { regex: /g50|tap|irg|tva|impot/i, type: 'g50_taxes' },
  { regex: /banque|bancaire/i, type: 'frais_bancaires' },
];

function mapTripSunSortie(categorie: string, ref: string, designation: string): string {
  const blob = `${categorie} ${ref} ${designation}`.toLowerCase();
  for (const { regex, type } of TRIP_SUN_DEPENSE_MAP) {
    if (regex.test(blob)) return type;
  }
  return 'imprevu_autre_sortie';
}

// =====================================================================
// Mappings B2C
// =====================================================================

const B2C_ENTREE_MAP: { regex: RegExp; type: string }[] = [
  { regex: /pizzalio/i, type: 'pizzalio' },
  { regex: /plomb/i, type: 'plomberie_ch_central' },
  { regex: /coiffure|coiffeur/i, type: 'coiffure' },
  { regex: /cake|patisserie/i, type: 'cake_design' },
  { regex: /^sp\b|suite paiement/i, type: 'suite_paiement_b2c' },
  { regex: /reservation|acompte/i, type: 'reservation_acompte_b2c' },
];

function mapB2CEntree(designation: string, ref: string): string {
  const blob = `${designation} ${ref}`.toLowerCase();
  for (const { regex, type } of B2C_ENTREE_MAP) {
    if (regex.test(blob)) return type;
  }
  return 'autre_formation_b2c';
}

const B2C_SORTIE_MAP: { regex: RegExp; type: string }[] = [
  { regex: /boost/i, type: 'boost_facebook_formation' },
  { regex: /salaire/i, type: 'salaire_b2c' },
  { regex: /achat|materiel/i, type: 'achat_materiel_formation' },
  { regex: /pdg|direction/i, type: 'pdg_b2c' },
  { regex: /dejeuner|repas/i, type: 'dejeuner_staff' },
  { regex: /location|loyer/i, type: 'location_b2c' },
  { regex: /assurance/i, type: 'assurance_b2c' },
  { regex: /prime|honoraire/i, type: 'honoraires_formateur_b2c' },
  { regex: /g50|facture|tap|impot/i, type: 'facture_g50_b2c' },
];

function mapB2CSortie(designation: string, ref: string): string {
  const blob = `${designation} ${ref}`.toLowerCase();
  for (const { regex, type } of B2C_SORTIE_MAP) {
    if (regex.test(blob)) return type;
  }
  return 'imprevu_b2c';
}

// =====================================================================
// Lookup-or-create helpers (idempotents par cache local + INSERT batch)
// =====================================================================

interface LookupCaches {
  tiers: Map<string, string>;
  teleops: Map<string, string>;
}

async function lookupOrCreateTier(
  caches: LookupCaches,
  entrepriseId: string,
  nomComplet: string,
  telephone: string | null,
): Promise<string | null> {
  if (!nomComplet.trim()) return null;
  const key = nomComplet.trim().toLowerCase();
  const cached = caches.tiers.get(key);
  if (cached) return cached;
  const { data: created, error } = await supabase
    .from('tiers')
    .insert({
      entreprise_id: entrepriseId,
      type: 'client_b2c',
      raison_sociale: nomComplet.trim(),
      telephone: telephone?.trim() || null,
    } as never)
    .select('id')
    .single();
  if (error) {
    // Si conflit de doublon, on retry avec une SELECT
    const { data: existing } = await supabase
      .from('tiers')
      .select('id')
      .eq('entreprise_id', entrepriseId)
      .ilike('raison_sociale', nomComplet.trim())
      .limit(1)
      .maybeSingle();
    if (existing) {
      caches.tiers.set(key, existing.id);
      return existing.id;
    }
    return null;
  }
  const id = (created as { id: string } | null)?.id ?? null;
  if (id) caches.tiers.set(key, id);
  return id;
}

async function lookupOrCreateTeleoperatrice(
  caches: LookupCaches,
  entrepriseId: string,
  prenom: string,
): Promise<string | null> {
  if (!prenom.trim()) return null;
  const key = prenom.trim().toLowerCase();
  const cached = caches.teleops.get(key);
  if (cached) return cached;
  const { data: created, error } = await supabase
    .from('staff_teleoperatrices')
    .insert({
      prenom: prenom.trim().toUpperCase(),
      entreprise_id: entrepriseId,
      is_active: true,
    } as never)
    .select('id')
    .single();
  if (error) {
    const { data: existing } = await supabase
      .from('staff_teleoperatrices')
      .select('id')
      .eq('entreprise_id', entrepriseId)
      .ilike('prenom', prenom.trim())
      .limit(1)
      .maybeSingle();
    const existingTyped = existing as { id: string } | null;
    if (existingTyped) {
      caches.teleops.set(key, existingTyped.id);
      return existingTyped.id;
    }
    return null;
  }
  const id = (created as { id: string } | null)?.id ?? null;
  if (id) caches.teleops.set(key, id);
  return id;
}

async function getDefaultCaisse(entrepriseId: string): Promise<string | null> {
  const { data } = await supabase
    .from('caisses')
    .select('id')
    .eq('entreprise_id', entrepriseId)
    .eq('is_active', true)
    .order('nom')
    .limit(1)
    .maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

async function getDefaultCompteCcp(entrepriseId: string): Promise<string | null> {
  const { data } = await supabase
    .from('comptes_bancaires')
    .select('id')
    .eq('entreprise_id', entrepriseId)
    .eq('is_active', true)
    .order('intitule')
    .limit(1)
    .maybeSingle();
  return (data as { id: string } | null)?.id ?? null;
}

async function resolveActivite(
  code: string,
): Promise<{ id: string; entreprise_id: string } | null> {
  const { data } = await supabase
    .from('activites')
    .select('id, entreprise_id')
    .eq('code', code)
    .eq('is_active', true)
    .maybeSingle();
  return (data as { id: string; entreprise_id: string } | null) ?? null;
}

// =====================================================================
// IMPORT TRIP SUN (26 colonnes)
// =====================================================================

export interface ImportCaisseTripSunRow {
  Date?: string | null;
  'N°'?: string | null;
  catégorie?: string | null;
  REF?: string | null;
  'NOM ET PRENOM'?: string | null;
  H?: string | null;
  DISIGNATION?: string | null;
  RETOUR?: string | null;
  'Recette service Evaluation preliminaire'?: string | null;
  'REC ACCOMP'?: string | null;
  'RECU CM'?: string | null;
  'Recette service'?: string | null;
  'Recette service visa Touristique'?: string | null;
  DEPENSE?: string | null;
  BON?: string | null;
  'SOLDE JOUR'?: string | null;
  'SOLDE FINAL'?: string | null;
  'nom de teleoperatrice'?: string | null;
  'nom de commercial'?: string | null;
  'NUM TEL'?: string | null;
  WILAYA?: string | null;
  'FRAIS GOUV'?: string | null;
  'CCP / BANQ'?: string | null;
  'FRAIS GOUV2'?: string | null;
  [key: string]: string | null | undefined;
}

export interface ImportCaisseProgress {
  current: number;
  total: number;
}

export interface ImportCaisseResult {
  importees: number;
  entrees: number;
  sorties: number;
  frais_gouv: number;
  tiers_crees: number;
  teleops_creees: number;
  errors: string[];
}

export interface ImportTripSunInput {
  rows: ImportCaisseTripSunRow[];
  onProgress?: (p: ImportCaisseProgress) => void;
}

export function useImportCaisseTripSun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportTripSunInput): Promise<ImportCaisseResult> => {
      const result: ImportCaisseResult = {
        importees: 0,
        entrees: 0,
        sorties: 0,
        frais_gouv: 0,
        tiers_crees: 0,
        teleops_creees: 0,
        errors: [],
      };

      const activite = await resolveActivite('trip_sun');
      if (!activite) {
        throw new Error(
          "Activité 'trip_sun' introuvable. Créez-la d'abord dans Paramètres → Activités (entreprise Sarl Trip Sun).",
        );
      }
      const caisseDefault = await getDefaultCaisse(activite.entreprise_id);
      const compteDefault = await getDefaultCompteCcp(activite.entreprise_id);
      if (!caisseDefault) {
        throw new Error(
          'Aucune caisse active pour Trip Sun. Créez-en une dans Paramètres → Banques & Caisses.',
        );
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      const caches: LookupCaches = { tiers: new Map(), teleops: new Map() };
      const tiersBefore = caches.tiers.size;
      const teleopsBefore = caches.teleops.size;

      const total = input.rows.length;
      for (let i = 0; i < total; i++) {
        const row = input.rows[i];
        if (!row) continue;
        const lineNo = i + 2;
        try {
          const date_op = buildTimestamp(row.Date, row.H);
          const cat = (row['catégorie'] ?? '').toString().toUpperCase();
          const ref = (row.REF ?? '').toString();
          const designation = (row.DISIGNATION ?? '').toString();
          const nomPrenom = (row['NOM ET PRENOM'] ?? '').toString();
          const tel = (row['NUM TEL'] ?? '').toString();
          const wilaya = (row.WILAYA ?? '').toString();
          const teleopPrenom = (row['nom de teleoperatrice'] ?? '').toString();
          const commercial = (row['nom de commercial'] ?? '').toString();

          const tierId = await lookupOrCreateTier(
            caches,
            activite.entreprise_id,
            nomPrenom,
            tel || null,
          );
          const teleopId = await lookupOrCreateTeleoperatrice(
            caches,
            activite.entreprise_id,
            teleopPrenom,
          );

          // Recettes (4 colonnes possibles → 4 entrées potentielles distinctes)
          type Recette = { col: string; type: string; label: string };
          const recettes: Recette[] = [
            {
              col: 'Recette service Evaluation preliminaire',
              type: 'evaluation_preliminaire',
              label: 'Évaluation préliminaire',
            },
            { col: 'REC ACCOMP', type: 'accompagnement_avec_suivi', label: 'Accompagnement' },
            { col: 'RECU CM', type: 'autre_entree_trip_sun', label: 'Reçu CM' },
            { col: 'Recette service', type: 'autre_entree_trip_sun', label: 'Recette service' },
            {
              col: 'Recette service visa Touristique',
              type: 'visa_touristique',
              label: 'Visa touristique',
            },
          ];
          const fraisGouv = parseNumberDZD(row['FRAIS GOUV']);
          const depense = parseNumberDZD(row.DEPENSE);

          const isCcp = /ccp|cheque|virement/i.test(cat);
          const canal: 'especes' | 'ccp' | 'banque' = isCcp ? 'ccp' : 'especes';
          const cibleCol =
            canal === 'especes'
              ? { caisse_id: caisseDefault }
              : { compte_bancaire_id: compteDefault ?? caisseDefault };
          // Si pas de compte CCP → on retombe sur caisse même pour les ccp/cheques (workaround setup)
          const finalCanal: 'especes' | 'ccp' | 'banque' =
            canal !== 'especes' && !compteDefault ? 'especes' : canal;
          const finalCible = finalCanal === 'especes' ? { caisse_id: caisseDefault } : cibleCol;

          for (const r of recettes) {
            const m = parseNumberDZD(row[r.col]);
            if (m <= 0) continue;
            const categorie = mapTypeCodeToCategorie(r.type);
            const obs = [
              `Type : ${r.label}`,
              wilaya && `Wilaya : ${wilaya}`,
              commercial && `Commercial : ${commercial}`,
              row.BON && `Bon : ${row.BON}`,
            ]
              .filter(Boolean)
              .join(' · ');
            const { error } = await supabase.from('operations').insert({
              entreprise_id: activite.entreprise_id,
              activite_id: activite.id,
              activite_beneficiaire_id: activite.id,
              activite_emettrice_id: activite.id,
              date_operation: date_op,
              sens: 'entree',
              canal: finalCanal,
              ...finalCible,
              categorie,
              tier_id: tierId,
              teleoperatrice_id: teleopId,
              devise_code: 'DZD',
              montant: m,
              taux_change: 1,
              montant_dzd: m,
              motif: r.label + (designation ? ` — ${designation}` : ''),
              observation: obs || null,
              reference: ref || null,
              contact_telephone: tel || null,
              statut: 'validee',
              created_by: createdBy,
            } as never);
            if (error) {
              result.errors.push(`L${lineNo} (${r.label}): ${error.message}`);
              continue;
            }
            result.entrees++;
            result.importees++;
          }

          if (fraisGouv > 0) {
            const { error } = await supabase.from('operations').insert({
              entreprise_id: activite.entreprise_id,
              activite_id: activite.id,
              activite_beneficiaire_id: activite.id,
              activite_emettrice_id: activite.id,
              date_operation: date_op,
              sens: 'entree',
              canal: finalCanal,
              ...finalCible,
              categorie: 'autre',
              tier_id: tierId,
              teleoperatrice_id: teleopId,
              devise_code: 'DZD',
              montant: fraisGouv,
              taux_change: 1,
              montant_dzd: fraisGouv,
              motif: 'Frais Gouvernement Canada (pass-through)',
              observation: 'Pass-through — pas de bénéfice net',
              reference: ref || null,
              statut: 'validee',
              created_by: createdBy,
            } as never);
            if (error) {
              result.errors.push(`L${lineNo} (frais gouv): ${error.message}`);
            } else {
              result.frais_gouv++;
              result.importees++;
            }
          }

          if (depense > 0) {
            const sortieType = mapTripSunSortie(cat, ref, designation);
            const categorie = mapTypeCodeToCategorie(sortieType);
            const obs = [
              `Type : ${sortieType.replace(/_/g, ' ')}`,
              commercial && `Commercial : ${commercial}`,
              row.BON && `Bon : ${row.BON}`,
            ]
              .filter(Boolean)
              .join(' · ');
            const { error } = await supabase.from('operations').insert({
              entreprise_id: activite.entreprise_id,
              activite_id: activite.id,
              activite_beneficiaire_id: activite.id,
              activite_emettrice_id: activite.id,
              date_operation: date_op,
              sens: 'sortie',
              canal: finalCanal,
              ...finalCible,
              categorie,
              tier_id: tierId,
              teleoperatrice_id: teleopId,
              devise_code: 'DZD',
              montant: depense,
              taux_change: 1,
              montant_dzd: depense,
              motif: designation || sortieType,
              observation: obs || null,
              reference: ref || null,
              statut: 'validee',
              created_by: createdBy,
            } as never);
            if (error) {
              result.errors.push(`L${lineNo} (sortie): ${error.message}`);
            } else {
              result.sorties++;
              result.importees++;
            }
          }
        } catch (e) {
          result.errors.push(`L${lineNo}: ${(e as Error).message}`);
        }
        if (input.onProgress && (i % 10 === 0 || i === total - 1)) {
          input.onProgress({ current: i + 1, total });
        }
      }

      result.tiers_crees = caches.tiers.size - tiersBefore;
      result.teleops_creees = caches.teleops.size - teleopsBefore;
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });
}

// =====================================================================
// IMPORT B2C (20 colonnes)
// =====================================================================

export interface ImportCaisseB2CRow {
  AL?: string | null;
  REF?: string | null;
  'NOM PRENOM'?: string | null;
  H?: string | null;
  DESIGNATION?: string | null;
  RETOUR?: string | null;
  RECETTE?: string | null;
  DEPENSE?: string | null;
  'SOLDE JOUR'?: string | null;
  'RECETTE RESERVE'?: string | null;
  'N °BON'?: string | null;
  CCP?: string | null;
  'R AP'?: string | null;
  'DATE N'?: string | null;
  'NUM TEL'?: string | null;
  WILAYA?: string | null;
  'D/FOR'?: string | null;
  'F/FOR'?: string | null;
  COM?: string | null;
  [key: string]: string | null | undefined;
}

export interface ImportB2CInput {
  rows: ImportCaisseB2CRow[];
  onProgress?: (p: ImportCaisseProgress) => void;
}

export function useImportCaisseB2C() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportB2CInput): Promise<ImportCaisseResult> => {
      const result: ImportCaisseResult = {
        importees: 0,
        entrees: 0,
        sorties: 0,
        frais_gouv: 0,
        tiers_crees: 0,
        teleops_creees: 0,
        errors: [],
      };

      const activite = await resolveActivite('formation_b2c');
      if (!activite) {
        throw new Error(
          "Activité 'formation_b2c' introuvable. Créez-la d'abord dans Paramètres → Activités (entreprise EURL Kost Group).",
        );
      }
      const caisseDefault = await getDefaultCaisse(activite.entreprise_id);
      const compteDefault = await getDefaultCompteCcp(activite.entreprise_id);
      if (!caisseDefault) {
        throw new Error(
          'Aucune caisse active pour Formation B2C. Créez-en une dans Paramètres → Banques & Caisses.',
        );
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      const createdBy = user?.id ?? null;

      const caches: LookupCaches = { tiers: new Map(), teleops: new Map() };
      const tiersBefore = caches.tiers.size;
      const total = input.rows.length;

      for (let i = 0; i < total; i++) {
        const row = input.rows[i];
        if (!row) continue;
        const lineNo = i + 2;
        try {
          const dateOp = buildTimestamp(row['DATE N'] ?? null, row.H ?? null);
          const ref = (row.REF ?? '').toString();
          const designation = (row.DESIGNATION ?? '').toString();
          const nomPrenom = (row['NOM PRENOM'] ?? '').toString();
          const tel = (row['NUM TEL'] ?? '').toString();
          const wilaya = (row.WILAYA ?? '').toString();
          const com = (row.COM ?? '').toString();
          const recette = parseNumberDZD(row.RECETTE);
          const depense = parseNumberDZD(row.DEPENSE);
          const ccpFlag = (row.CCP ?? '').toString().trim();

          const tierId = await lookupOrCreateTier(
            caches,
            activite.entreprise_id,
            nomPrenom,
            tel || null,
          );

          const isCcp = !!ccpFlag && /[a-z0-9]/i.test(ccpFlag);
          const canal: 'especes' | 'ccp' | 'banque' = isCcp && compteDefault ? 'ccp' : 'especes';
          const finalCible =
            canal === 'especes'
              ? { caisse_id: caisseDefault }
              : { compte_bancaire_id: compteDefault ?? caisseDefault };

          if (recette > 0) {
            const sousType = mapB2CEntree(designation, ref);
            const categorie = mapTypeCodeToCategorie(sousType);
            const obs = [
              `Type : ${sousType.replace(/_/g, ' ')}`,
              wilaya && `Wilaya : ${wilaya}`,
              com && `Commercial : ${com}`,
              row['N °BON'] && `Bon : ${row['N °BON']}`,
            ]
              .filter(Boolean)
              .join(' · ');
            const { error } = await supabase.from('operations').insert({
              entreprise_id: activite.entreprise_id,
              activite_id: activite.id,
              activite_beneficiaire_id: activite.id,
              activite_emettrice_id: activite.id,
              date_operation: dateOp,
              sens: 'entree',
              canal,
              ...finalCible,
              categorie,
              tier_id: tierId,
              devise_code: 'DZD',
              montant: recette,
              taux_change: 1,
              montant_dzd: recette,
              motif: designation || sousType,
              observation: obs || null,
              reference: ref || null,
              contact_telephone: tel || null,
              statut: 'validee',
              created_by: createdBy,
            } as never);
            if (error) {
              result.errors.push(`L${lineNo} (recette): ${error.message}`);
            } else {
              result.entrees++;
              result.importees++;
            }
          }

          if (depense > 0) {
            const sousType = mapB2CSortie(designation, ref);
            const categorie = mapTypeCodeToCategorie(sousType);
            const obs = [`Type : ${sousType.replace(/_/g, ' ')}`, com && `Commercial : ${com}`]
              .filter(Boolean)
              .join(' · ');
            const { error } = await supabase.from('operations').insert({
              entreprise_id: activite.entreprise_id,
              activite_id: activite.id,
              activite_beneficiaire_id: activite.id,
              activite_emettrice_id: activite.id,
              date_operation: dateOp,
              sens: 'sortie',
              canal,
              ...finalCible,
              categorie,
              tier_id: tierId,
              devise_code: 'DZD',
              montant: depense,
              taux_change: 1,
              montant_dzd: depense,
              motif: designation || sousType,
              observation: obs || null,
              reference: ref || null,
              statut: 'validee',
              created_by: createdBy,
            } as never);
            if (error) {
              result.errors.push(`L${lineNo} (sortie): ${error.message}`);
            } else {
              result.sorties++;
              result.importees++;
            }
          }
        } catch (e) {
          result.errors.push(`L${lineNo}: ${(e as Error).message}`);
        }
        if (input.onProgress && (i % 10 === 0 || i === total - 1)) {
          input.onProgress({ current: i + 1, total });
        }
      }

      result.tiers_crees = caches.tiers.size - tiersBefore;
      return result;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['operations'] });
      void qc.invalidateQueries({ queryKey: ['soldes'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });
}
